import dayjs from 'dayjs';
import { z } from 'zod';
import { DEFAULT_COUNTRY } from '../common/constants';
import { LEAD_TYPE_LABELS, LeadType } from '../common/enums';
import { enumLabels } from '../common/fields';
import {
  BirthdaySchema,
  CountrySchema,
  EmailSchema,
  GradYearSchema,
  HumanNameSchema,
  PhoneNumberSchema,
  PostcodeSchema,
  SchoolNameSchema,
  SearchStringSchema,
  StreetAddressSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';
import {
  DBSchoolLabelSchema,
  DBSchoolSchoolIdSchema,
  DBSchoolShortLabelSchema,
} from '../schools/schemas';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

// Lead attributes are intentionally and exclusively loose with a fallback option
// to ensure that no calls for creating a lead fail due to validation errors (from eg. enrolment
// forms being filled out)

// A union is used instead of the generic schema exclusively, as certain schemas like phone number
// schema produces a transformed output, which is preferred when possible
export const DBLeadTypeSchema = z.enum(LeadType).register(enumLabels, { labels: LEAD_TYPE_LABELS });
export const DBLeadLeadIdSchema = UUIDSchema;
export const DBLeadBirthdaySchema = z.union([BirthdaySchema, z.number()]);
export const DBLeadCountrySchema = z.union([CountrySchema, z.string()]);
export const DBLeadCreationDatetimeSchema = UnixTimestampSchema;
export const DBLeadFirstNameSchema = z.union([HumanNameSchema, z.string()]);
export const DBLeadLastNameSchema = z.union([HumanNameSchema, z.string()]);
export const DBLeadGradYearSchema = z.union([
  GradYearSchema,
  z
    .string()
    .transform(val => {
      const parsed = Number(val);
      // To maintain typing of graduation year as a number
      return Number.isNaN(parsed) ? 0 : parsed;
    })
    .pipe(z.number()),
  z.number(),
]);
export const DBLeadSchoolSchema = z.union([SchoolNameSchema, z.string()]);
// Unlike the other lead attributes this one is strict: it is set by staff picking a
// school from the schools table, never by an enrolment form's free text
export const DBLeadSchoolIdSchema = z.lazy(() => DBSchoolSchoolIdSchema);
export const DBLeadSchoolLabelSchema = z.lazy(() => DBSchoolLabelSchema);
export const DBLeadSchoolShortLabelSchema = z.lazy(() => DBSchoolShortLabelSchema);
export const DBLeadEmailSchema = z.union([EmailSchema, z.string()]);
export const DBLeadPhoneNumberSchema = z.union([PhoneNumberSchema, z.string()]);
export const DBLeadParentFirstNameSchema = z.union([HumanNameSchema, z.string()]);
export const DBLeadParentLastNameSchema = z.union([HumanNameSchema, z.string()]);
export const DBLeadParentEmailSchema = z.union([EmailSchema, z.string()]);
export const DBLeadParentPhoneNumberSchema = z.union([PhoneNumberSchema, z.string()]);
export const DBLeadStreetAddressSchema = z.union([StreetAddressSchema, z.string()]);
export const DBLeadPostcodeSchema = z.union([PostcodeSchema, z.string()]);
export const DBLeadSearchStrSchema = SearchStringSchema;

export const DBLeadSchema = z.object({
  type: DBLeadTypeSchema,
  leadId: DBLeadLeadIdSchema,
  creationDatetime: DBLeadCreationDatetimeSchema,
  firstName: DBLeadFirstNameSchema.optional(),
  lastName: DBLeadLastNameSchema.optional(),
  gradYear: DBLeadGradYearSchema.optional(),
  school: DBLeadSchoolSchema.optional(),
  schoolId: DBLeadSchoolIdSchema.optional(),
  schoolLabel: DBLeadSchoolLabelSchema.optional(),
  schoolShortLabel: DBLeadSchoolShortLabelSchema.optional(),
  email: DBLeadEmailSchema.optional(),
  phoneNumber: DBLeadPhoneNumberSchema.optional(),
  birthday: DBLeadBirthdaySchema.optional(),
  parentFirstName: DBLeadParentFirstNameSchema.optional(),
  parentLastName: DBLeadParentLastNameSchema.optional(),
  parentEmail: DBLeadParentEmailSchema.optional(),
  parentPhoneNumber: DBLeadParentPhoneNumberSchema.optional(),
  streetAddress: DBLeadStreetAddressSchema.optional(),
  country: DBLeadCountrySchema,
  postcode: DBLeadPostcodeSchema.optional(),
  searchStr: DBLeadSearchStrSchema,
});

/**
 * Client Schemas
 */

export const LeadTypeSchema = DBLeadTypeSchema;
export const LeadLeadIdSchema = DBLeadLeadIdSchema;
export const LeadBirthdaySchema = DBLeadBirthdaySchema;
export const LeadCountrySchema = DBLeadCountrySchema;
export const LeadCreationDatetimeSchema = DBLeadCreationDatetimeSchema;
export const LeadFirstNameSchema = DBLeadFirstNameSchema;
export const LeadLastNameSchema = DBLeadLastNameSchema;
export const LeadGradYearSchema = DBLeadGradYearSchema;
export const LeadSchoolSchema = DBLeadSchoolSchema;
export const LeadSchoolIdSchema = DBLeadSchoolIdSchema;
export const LeadSchoolLabelSchema = DBLeadSchoolLabelSchema;
export const LeadSchoolShortLabelSchema = DBLeadSchoolShortLabelSchema;
export const LeadEmailSchema = DBLeadEmailSchema;
export const LeadPhoneNumberSchema = DBLeadPhoneNumberSchema;
export const LeadParentFirstNameSchema = DBLeadParentFirstNameSchema;
export const LeadParentLastNameSchema = DBLeadParentLastNameSchema;
export const LeadParentEmailSchema = DBLeadParentEmailSchema;
export const LeadParentPhoneNumberSchema = DBLeadParentPhoneNumberSchema;
export const LeadStreetAddressSchema = DBLeadStreetAddressSchema;
export const LeadPostcodeSchema = DBLeadPostcodeSchema;
export const LeadSearchStrSchema = DBLeadSearchStrSchema;

export const LeadSchema = z.object({
  type: LeadTypeSchema.meta({ title: 'Lead type' }),
  leadId: LeadLeadIdSchema.meta({ title: 'Lead ID' }),
  creationDatetime: LeadCreationDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Created at',
  }),
  firstName: LeadFirstNameSchema.optional().meta({ title: 'First name' }),
  lastName: LeadLastNameSchema.optional().meta({ title: 'Last name' }),
  gradYear: LeadGradYearSchema.optional().meta({ title: 'Grad year' }),
  school: LeadSchoolSchema.optional().meta({ title: 'School (free text)' }),
  schoolId: LeadSchoolIdSchema.optional().meta({ title: 'School' }),
  schoolLabel: LeadSchoolLabelSchema.optional().meta({ title: 'School name' }),
  schoolShortLabel: LeadSchoolShortLabelSchema.optional().meta({ title: 'School short name' }),
  email: LeadEmailSchema.optional().meta({ title: 'Email' }),
  phoneNumber: LeadPhoneNumberSchema.optional().meta({ title: 'Phone number' }),
  birthday: LeadBirthdaySchema.optional().meta({ title: 'Birthday' }),
  parentFirstName: LeadParentFirstNameSchema.optional().meta({ title: 'Parent first name' }),
  parentLastName: LeadParentLastNameSchema.optional().meta({ title: 'Parent last name' }),
  parentEmail: LeadParentEmailSchema.optional().meta({ title: 'Parent email' }),
  parentPhoneNumber: LeadParentPhoneNumberSchema.optional().meta({
    title: 'Parent phone number',
  }),
  streetAddress: LeadStreetAddressSchema.optional().meta({ title: 'Street address' }),
  country: LeadCountrySchema.default(DEFAULT_COUNTRY).meta({ title: 'Country' }),
  postcode: LeadPostcodeSchema.optional().meta({ title: 'Postcode' }),
  searchStr: LeadSearchStrSchema.default(''),
});
